// Brand flow ([data-brand-flow], a canvas in the CTA): rounded night, rose and yellow blocks drawn by a WebGL shader.
// They drift slowly at the edges, blocks of one colour melt into each other where they meet (smooth union), the
// yellow layer lies on top with a soft shadow, and the layers slide past each other while the CTA crosses the window
// (the section's "scrollprogress"). The pointer is a small rose drop that the blocks lean towards and melt into.
// The text area stays nearly free, so the white text keeps its contrast. The section gets .has-flow once the first
// frame is drawn, which hides the static pattern; without WebGL the pattern stays. Runs only while on screen; with
// reduced motion it draws one still frame.
import { reducedMotion } from './motion.js'

const COLORS = {
	deep: [0.055, 0.058, 0.075], // darker than night: blocks set into the background
	rose: [0.902, 0.0, 0.494], // --color-rose
	yellow: [0.925, 0.878, 0.573], // --color-yellow
}

// positions as fractions of the canvas half width (x) and half height (y), half sizes in canvas heights; `depth` is
// how far the block moves with the scroll progress
const BLOCKS = {
	deep: [
		{ x: -0.82, y: 0.3, w: 0.16, h: 0.22, depth: 0.12 },
		{ x: 0.78, y: -0.28, w: 0.2, h: 0.14, depth: 0.12 },
	],
	rose: [
		{ x: -0.96, y: -0.18, w: 0.12, h: 0.2, depth: 0.3 },
		{ x: -0.7, y: 0.5, w: 0.1, h: 0.07, depth: 0.3 },
		{ x: 0.92, y: 0.38, w: 0.14, h: 0.12, depth: 0.3 },
		{ x: 0.62, y: -0.5, w: 0.08, h: 0.1, depth: 0.3 },
	],
	yellow: [
		{ x: -0.8, y: -0.42, w: 0.09, h: 0.13, depth: -0.45 },
		{ x: 0.76, y: 0.06, w: 0.08, h: 0.18, depth: -0.45 },
		{ x: 0.99, y: -0.12, w: 0.06, h: 0.06, depth: -0.45 },
	],
}

const VERTEX = `
attribute vec2 aPosition;
void main() { gl_Position = vec4(aPosition, 0.0, 1.0); }
`

const FRAGMENT = `
precision mediump float;
uniform vec2 uRes;
uniform vec4 uDeep[2];
uniform vec4 uRose[4];
uniform vec4 uYellow[3];
uniform vec4 uText;
uniform vec3 uPointer;
uniform float uTime;
uniform vec3 uDeepColor;
uniform vec3 uRoseColor;
uniform vec3 uYellowColor;

float box(vec2 p, vec4 b, float r) {
	vec2 q = abs(p - b.xy) - b.zw + r;
	return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}

float smin(float a, float b, float k) {
	float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
	return mix(b, a, h) - k * h * (1.0 - h);
}

float hash(vec2 p) {
	return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

// paints one layer over the colour so far (premultiplied): a soft shadow around it, then the block, darker at its
// rim and lighter inside, so it reads as a raised, rounded tile
vec4 layer(vec4 below, float d, vec3 color, float shadow, float px) {
	float s = (1.0 - smoothstep(0.0, 0.09, d)) * shadow;
	below = vec4(below.rgb * (1.0 - s), s + below.a * (1.0 - s));
	float a = 1.0 - smoothstep(-px, px, d);
	vec3 c = color * (0.84 + 0.16 * smoothstep(0.0, 0.07, -d));
	return vec4(c * a + below.rgb * (1.0 - a), a + below.a * (1.0 - a));
}

void main() {
	vec2 uv = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;
	float px = 1.2 / uRes.y;
	float r = 0.04;
	float k = 0.1;

	float deep = 1e3;
	for (int i = 0; i < 2; i++) deep = smin(deep, box(uv, uDeep[i], r), k);
	float rose = 1e3;
	for (int i = 0; i < 4; i++) rose = smin(rose, box(uv, uRose[i], r), k);
	rose = smin(rose, length(uv - uPointer.xy) - 0.045 * uPointer.z + (1.0 - uPointer.z), 0.14);
	float yellow = 1e3;
	for (int i = 0; i < 3; i++) yellow = smin(yellow, box(uv, uYellow[i], r), k);

	vec4 color = vec4(0.0);
	color = layer(color, deep, uDeepColor, 0.0, px);
	color = layer(color, rose, uRoseColor, 0.35, px);
	color = layer(color, yellow, uYellowColor, 0.5, px);

	// the text area: blocks fade to a trace there
	float free = smoothstep(0.0, 0.05, box(uv, uText, 0.04));
	color *= mix(0.07, 1.0, free);
	// a little grain against banding
	color.rgb += (hash(gl_FragCoord.xy + fract(uTime)) - 0.5) * 0.025 * color.a;
	gl_FragColor = color;
}
`

export function initBrandFlow() {
	document.querySelectorAll('[data-brand-flow]').forEach(setupFlow)
}

function setupFlow(canvas) {
	const section = canvas.parentElement
	const text = section.querySelector('.yn-article')
	const gl = canvas.getContext('webgl', { premultipliedAlpha: true, antialias: false })
	if (!gl) return
	const program = createProgram(gl)
	if (!program) return

	gl.useProgram(program)
	const buffer = gl.createBuffer()
	gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
	gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
	const position = gl.getAttribLocation(program, 'aPosition')
	gl.enableVertexAttribArray(position)
	gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

	const uniform = (name) => gl.getUniformLocation(program, name)
	const u = {
		res: uniform('uRes'),
		deep: uniform('uDeep'),
		rose: uniform('uRose'),
		yellow: uniform('uYellow'),
		text: uniform('uText'),
		pointer: uniform('uPointer'),
		time: uniform('uTime'),
	}
	gl.uniform3fv(uniform('uDeepColor'), COLORS.deep)
	gl.uniform3fv(uniform('uRoseColor'), COLORS.rose)
	gl.uniform3fv(uniform('uYellowColor'), COLORS.yellow)

	let width = 0
	let height = 0
	let progress = 0.5
	let time = 0
	let last = 0
	let frame = 0
	let visible = false
	const pointer = { x: 0, y: 0, on: 0, target: 0 }

	// the box around the text lines and buttons, in shader units (canvas heights from the centre, y up); the
	// lines are measured one by one, since the article's own box spans the whole container
	const textBox = () => {
		if (!text) return [0, 0, 0, 0]
		const rects = [...text.querySelectorAll('.button, .yn-buttons a')].map((button) => button.getBoundingClientRect())
		const range = document.createRange()
		const walker = document.createTreeWalker(text, NodeFilter.SHOW_TEXT)
		while (walker.nextNode()) {
			if (!walker.currentNode.textContent.trim()) continue
			range.selectNodeContents(walker.currentNode)
			rects.push(...range.getClientRects())
		}
		if (!rects.length) return [0, 0, 0, 0]
		const left = Math.min(...rects.map((rect) => rect.left))
		const right = Math.max(...rects.map((rect) => rect.right))
		const top = Math.min(...rects.map((rect) => rect.top))
		const bottom = Math.max(...rects.map((rect) => rect.bottom))
		const outer = canvas.getBoundingClientRect()
		const h = outer.height || 1
		return [
			((left + right) / 2 - outer.left - outer.width / 2) / h,
			-((top + bottom) / 2 - outer.top - outer.height / 2) / h,
			(right - left) / 2 / h,
			(bottom - top) / 2 / h,
		]
	}

	const resize = () => {
		const ratio = Math.min(window.devicePixelRatio || 1, 1.5)
		width = Math.max(1, Math.round(canvas.clientWidth * ratio))
		height = Math.max(1, Math.round(canvas.clientHeight * ratio))
		canvas.width = width
		canvas.height = height
		gl.viewport(0, 0, width, height)
		gl.uniform2f(u.res, width, height)
		gl.uniform4fv(u.text, textBox())
	}

	const place = (blocks, aspect, scale, shift) => {
		const data = []
		blocks.forEach((block, index) => {
			const phase = index * 1.7 + block.x * 3
			let x = block.x * (aspect / 2) + Math.sin(time * 0.23 + phase) * 0.05 * scale
			// narrow screens: the text fills the width, so the blocks move into the strips above and below it
			const rest = aspect < 1 ? Math.sign(block.y) * (0.44 + Math.abs(block.y) * 0.08) : block.y * 0.5
			let y = rest + Math.cos(time * 0.19 + phase) * 0.045 + (progress - 0.5) * block.depth * shift
			// lean towards the pointer
			const dx = pointer.x - x
			const dy = pointer.y - y
			const pull = 0.22 * pointer.on * Math.exp(-Math.hypot(dx, dy) * 4)
			x += dx * pull
			y += dy * pull
			data.push(x, y, block.w * scale, block.h * scale)
		})
		return new Float32Array(data)
	}

	const draw = () => {
		const aspect = width / height
		// narrow screens: smaller blocks, and they move less with the scroll (the CTA is taller there)
		const scale = Math.min(1, Math.max(0.5, aspect / 2.2))
		const shift = Math.min(1, aspect)
		pointer.on += (pointer.target - pointer.on) * 0.08
		gl.uniform4fv(u.deep, place(BLOCKS.deep, aspect, scale, shift))
		gl.uniform4fv(u.rose, place(BLOCKS.rose, aspect, scale, shift))
		gl.uniform4fv(u.yellow, place(BLOCKS.yellow, aspect, scale, shift))
		gl.uniform3f(u.pointer, pointer.x, pointer.y, pointer.on)
		gl.uniform1f(u.time, time)
		gl.clearColor(0, 0, 0, 0)
		gl.clear(gl.COLOR_BUFFER_BIT)
		gl.drawArrays(gl.TRIANGLES, 0, 3)
		section.classList.add('has-flow')
	}

	const loop = (now) => {
		frame = 0
		// at most 1/20 s per frame, so a tab coming back does not jump
		time += Math.min(0.05, (now - (last || now)) / 1000)
		last = now
		draw()
		if (visible && !reducedMotion.matches) frame = requestAnimationFrame(loop)
	}
	const start = () => {
		if (frame) return
		last = 0
		if (reducedMotion.matches) {
			time = 0
			pointer.target = pointer.on = 0
			draw()
			return
		}
		frame = requestAnimationFrame(loop)
	}
	const stop = () => {
		cancelAnimationFrame(frame)
		frame = 0
	}

	section.addEventListener('scrollprogress', ({ detail }) => {
		progress = detail
	})
	section.addEventListener('pointermove', (event) => {
		const box = canvas.getBoundingClientRect()
		pointer.x = (event.clientX - box.left - box.width / 2) / box.height
		pointer.y = -(event.clientY - box.top - box.height / 2) / box.height
		pointer.target = event.pointerType === 'mouse' ? 1 : 0
	})
	section.addEventListener('pointerleave', () => {
		pointer.target = 0
	})
	canvas.addEventListener('webglcontextlost', () => {
		stop()
		section.classList.remove('has-flow')
	})

	new ResizeObserver(() => {
		resize()
		if (!frame) draw()
	}).observe(canvas)
	new IntersectionObserver(([entry]) => {
		visible = entry.isIntersecting
		visible ? start() : stop()
	}).observe(canvas)
	reducedMotion.addEventListener('change', () => {
		stop()
		if (visible) start()
	})
}

function createProgram(gl) {
	const compile = (type, source) => {
		const shader = gl.createShader(type)
		gl.shaderSource(shader, source)
		gl.compileShader(shader)
		if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader
		console.warn('brand-flow:', gl.getShaderInfoLog(shader))
		return null
	}
	const vertex = compile(gl.VERTEX_SHADER, VERTEX)
	const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT)
	if (!vertex || !fragment) return null
	const program = gl.createProgram()
	gl.attachShader(program, vertex)
	gl.attachShader(program, fragment)
	gl.linkProgram(program)
	return gl.getProgramParameter(program, gl.LINK_STATUS) ? program : null
}
